import { lazy } from 'react'

import { Features } from 'generated/featureDefinitions'
import { EngagementTabsDocument, type EngagementTabsQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'
import { type NavigationRoutesFn, buildModuleRoute } from 'modules/core/lib/react-router'

import { engagementInsightsRoutes } from 'modules/engagement_insights/routes'
import smartSurveysRoutes from 'modules/engagement_surveys/routes'
import enpsRoutes from 'modules/enps/routes'
import { oneOnOnesRoutes } from 'modules/meetings/routes/oneOnOnesRoutes'

const RedirectToRoutePage = lazy(() => import('modules/engagement/pages/RedirectToRoutePage'))
const RootPage = lazy(() => import('modules/engagement/pages/RootPage'))
const PulseRootPage = lazy(() => import('modules/engagement/pages/pulse/RootPage'))
const RedirectPulse = lazy(() => import('modules/engagement/pages/RedirectPulse'))
const PulsePage = lazy(() => import('modules/engagement/pages/pulse'))
const AiReportPage = lazy(() => import('modules/engagement/pages/pulseAiReport'))

const featureEnabled = (data: EngagementTabsQuery, featureName: string) => {
  return (
    data.features.featuresConnection?.edges.find((feature) => feature.node.name === featureName)
      ?.node.enabled ?? false
  )
}

export const engagementRoutes: NavigationRoutesFn = (navigation) => {
  return [
    buildModuleRoute({
      path: navigation.engagement.path,
      element: <RootPage />,
      handle: {
        module: 'engagement',
        moduleName: () => i18n.t('sidebar.engagement'),
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: EngagementTabsDocument,
            variables: {
              companyId: session.company.id,
              featureAvailabilityIds: [Features.engagement_employee_pulse_questions],
            },
          }).map(({ data }) => {
            const settingsItems: { label: string; href: string }[] = []
            const settingsManage = data?.engagement?._policies.settingsManage.can
            const smartSurveysVisible =
              data?.surveys?._policies.sidebarSeeSmartSurveysSubLevelItem.can ?? false

            if (
              data?.surveys?._policies.sidebarSeeEngagementSurveysSubLevelItem.can === true &&
              data?.surveys?._policies.createSurveyTemplates.can === true
            ) {
              settingsItems.push({
                label: i18n.t('surveys.templates'),
                href: navigation.settings.surveys.fullPath(),
              })
            }

            const pulseFeature =
              isFeatureAvailabilityEnabled(
                data?.companies?.featureAvailabilitiesConnection?.nodes || [],
                Features.engagement_employee_pulse_questions
              ) || featureEnabled(data, 'engagement_employee_pulse_questions')

            const meetingsVisible = data?.meetings?._policies.seeMeetings.can

            if ((pulseFeature || meetingsVisible) && settingsManage) {
              settingsItems.push({
                label: i18n.t('engagement.settings.title'),
                href: navigation.settings.engagement.fullPath(),
              })
            }

            const upsellFlowEnabled = data.upsell._policies.canRequestUpsell.can ?? false

            const enpsVisible = data?.enps?._policies.sidebarSeeEngagementEnpsSubLevelItem.can
            const insightsVisible =
              data?.engagementInsights?._policies.sidebarSeeEngagementInsightsSubLevelItem.can

            return {
              title: i18n.t('sidebar.engagement'),
              settings: settingsItems.length > 0 ? { items: settingsItems } : undefined,
              subNavigation: [
                {
                  id: 'navigation.engagement.one_on_ones.fullPath',
                  title: i18n.t('engagement.one_on_ones.tab_title'),
                  href: navigation.engagement.one_on_ones.fullPath(),
                  isVisible: meetingsVisible,
                },
                {
                  id: 'navigation.engagement.pulse.fullPath',
                  title: i18n.t('engagement.pulse.title'),
                  href: navigation.engagement.pulse.fullPath(),
                  isVisible: data?.engagement?._policies.seePulseDashboard.can && pulseFeature,
                },
                {
                  id: 'navigation.engagement.smart_surveys.fullPath',
                  title: i18n.t('surveys.index.title'),
                  href: navigation.engagement.smart_surveys.fullPath(),
                  isVisible: smartSurveysVisible,
                  variant: upsellFlowEnabled && !smartSurveysVisible ? 'upsell' : undefined,
                },
                {
                  id: 'navigation.engagement.enps.fullPath',
                  title: i18n.t('enps.index.title'),
                  href: navigation.engagement.enps.fullPath(),
                  isVisible: enpsVisible || upsellFlowEnabled,
                  variant: upsellFlowEnabled && !enpsVisible ? 'upsell' : undefined,
                },
                {
                  id: 'navigation.engagement.insights.fullPath',
                  title: i18n.t('surveys.insights'),
                  href: navigation.engagement.insights.fullPath(),
                  index: true,
                  isVisible: insightsVisible || upsellFlowEnabled,
                  variant: upsellFlowEnabled && !insightsVisible ? 'upsell' : undefined,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <RedirectToRoutePage />,
        },
        ...engagementInsightsRoutes(navigation),
        {
          path: navigation.engagement.pulse.path,
          element: <PulseRootPage />,
          handle: {
            meta: ({ watchQuery, session, navigate }) => {
              return watchQuery({
                query: EngagementTabsDocument,
                variables: {
                  companyId: session.company.id,
                  featureAvailabilityIds: [Features.engagement_pulse_ai_report],
                },
              }).map(({ data }) => {
                if (!data.engagement._policies.seePulseDashboard.can) {
                  navigate(navigation.engagement.fullPath())
                }

                const pulseAiReportFeature =
                  isFeatureAvailabilityEnabled(
                    data?.companies?.featureAvailabilitiesConnection?.nodes || [],
                    Features.engagement_pulse_ai_report
                  ) || featureEnabled(data, 'engagement_employee_pulse_ai_report')

                const subNavigation = pulseAiReportFeature
                  ? [
                      {
                        id: 'navigation.engagement.pulse.participation.fullPath',
                        title: i18n.t('engagement.pulse.participation.title'),
                        href: navigation.engagement.pulse.participation.fullPath(),
                      },
                      {
                        id: 'navigation.engagement.pulse.aiReport.fullPath',
                        title: i18n.t('engagement.pulse.pulse_ai_report.title'),
                        href: navigation.engagement.pulse.aiReport.fullPath(),
                        isVisible: pulseAiReportFeature,
                      },
                    ]
                  : []

                return {
                  title: i18n.t('engagement.pulse.title'),
                  subNavigation,
                }
              })
            },
          },
          children: [
            { path: '', element: <RedirectPulse />, index: true },
            { path: navigation.engagement.pulse.participation.path, element: <PulsePage /> },
            {
              path: navigation.engagement.pulse.aiReport.path,
              element: <AiReportPage />,
              handle: {
                meta: ({ watchQuery, session, navigate }) => {
                  return watchQuery({
                    query: EngagementTabsDocument,
                    variables: {
                      companyId: session.company.id,
                      featureAvailabilityIds: [Features.engagement_pulse_ai_report],
                    },
                  }).map(({ data }) => {
                    const pulseAiReportFeature =
                      isFeatureAvailabilityEnabled(
                        data?.companies?.featureAvailabilitiesConnection?.nodes || [],
                        Features.engagement_pulse_ai_report
                      ) || featureEnabled(data, 'engagement_employee_pulse_ai_report')

                    if (!pulseAiReportFeature) {
                      navigate(navigation.engagement.pulse.participation.fullPath())
                      return { title: '' }
                    }

                    return {
                      title: i18n.t('engagement.pulse.pulse_ai_report.title'),
                    }
                  })
                },
              },
            },
          ],
        },
        ...smartSurveysRoutes(navigation),
        ...enpsRoutes(navigation),
        ...oneOnOnesRoutes(navigation),
      ],
    }),
  ]
}
